function e(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function amountToWords(amount) {
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
    "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  function chunk(n) {
    if (n === 0) return "";
    if (n < 20)  return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + chunk(n % 100) : "");
  }
  function convert(n) {
    if (n === 0) return "Zero";
    let out = "";
    if (n >= 10000000) { out += chunk(Math.floor(n / 10000000)) + " Crore "; n %= 10000000; }
    if (n >= 100000)   { out += chunk(Math.floor(n / 100000))   + " Lakh ";  n %= 100000;   }
    if (n >= 1000)     { out += chunk(Math.floor(n / 1000))     + " Thousand "; n %= 1000;  }
    if (n > 0)         { out += chunk(n); }
    return out.trim();
  }
  return "Rupees " + convert(Math.floor(Number(amount) || 0)) + " Only";
}

function renderCompanyBlock(company) {
  if (!company || !company.CompanyName) return "";
  const addr = [company.AddressLine1, company.AddressLine2, company.City, company.State, company.PinCode].filter(Boolean);
  const contact = [
    company.Phone1 ? `Ph: ${company.Phone1}` : "",
    company.Email  ? `Email: ${company.Email}` : "",
    company.GSTIN  ? `GSTIN: ${company.GSTIN}` : "",
  ].filter(Boolean);
  const details = `
    <div class="co-name">${e(company.DisplayName || company.CompanyName)}</div>
    ${addr.length    ? `<div class="co-det">${e(addr.join(", "))}</div>` : ""}
    ${contact.length ? `<div class="co-det">${e(contact.join("  |  "))}</div>` : ""}`;

  if (!company.LogoUrl) return details;
  return `
    <div class="co-brand">
      <img class="co-logo" src="${e(company.LogoUrl)}" alt="${e(company.DisplayName || company.CompanyName)} logo" />
      <div>${details}</div>
    </div>`;
}

function renderInfoRows(rows) {
  return (rows || []).filter(r => r && r.value).map(r =>
    `<tr><td class="il">${e(r.label)}</td><td class="iv">: ${e(r.value)}</td></tr>`
  ).join("");
}

function renderHeaderCells(columns) {
  return columns.map(col => {
    const cls = col.className ? ` class="${e(col.className)}"` : "";
    const sty = col.style    ? ` style="${e(col.style)}"` : "";
    return `<th${cls}${sty}>${e(col.label)}</th>`;
  }).join("");
}

function renderBodyRows(rows, minRowCount = 12, columnCount = 0) {
  const bodyRows = (rows || []).map((row, ri) => {
    const cells = row.map((cell, ci) => {
      const val = cell?.html ?? e(cell?.text ?? "");
      const cls = cell?.className ? ` class="${e(cell.className)}"` : "";
      const sty = cell?.style    ? ` style="${e(cell.style)}"` : "";
      return `<td${cls}${sty}>${ci === 0 && !cell?.html ? e(cell?.text ?? String(ri + 1)) : val}</td>`;
    }).join("");
    return `<tr>${cells}</tr>`;
  });

  const fillerCount = Math.max(0, minRowCount - (rows || []).length);
  const fillerCells = Array.from({ length: columnCount }, () => `<td>&nbsp;</td>`).join("");
  const fillerRows = Array.from({ length: fillerCount }, () => `<tr class="fill-row">${fillerCells}</tr>`);
  return bodyRows.concat(fillerRows).join("");
}

function renderSubTotal(subTotalRow) {
  if (!subTotalRow || !subTotalRow.length) return "";
  const cells = subTotalRow.map(cell => {
    const val = cell?.html ?? e(cell?.text ?? "");
    const cls = cell?.className ? ` class="${e(cell.className)}"` : "";
    return `<td${cls}>${val}</td>`;
  }).join("");
  return `<tfoot><tr>${cells}</tr></tfoot>`;
}

function renderTotals(totals) {
  return (totals || []).map((row, i) =>
    `<tr class="${i === totals.length - 1 ? "tnet" : ""}">
      <td class="tl">${e(row.label)}</td>
      <td class="tv">${e(row.value)}</td>
    </tr>`
  ).join("");
}

function renderBankRows(company) {
  const rows = [
    { label: "Bank Name", value: company?.BankName },
    { label: "Account No", value: company?.AccountNo },
    { label: "IFSC Code", value: company?.IFSCCode },
    { label: "UPI ID", value: company?.UPIId },
  ].filter(row => row.value);

  if (!rows.length) {
    return `<tr><td class="iv muted" colspan="2">Bank details not configured.</td></tr>`;
  }

  return renderInfoRows(rows);
}

function renderTerms(note) {
  const text = String(note || "").trim();
  if (!text) return "";

  const points = text.split(/\r?\n+/)
    .flatMap(line => line.split(/(?=\s*(?:\d+[\).]|[-*])\s+)/))
    .map(line => line.trim())
    .filter(Boolean);

  return `
    <div class="terms-panel">
      <div class="wt">Terms &amp; Conditions</div>
      <div class="terms-list">
        ${points.map(point => `<div>${e(point)}</div>`).join("")}
      </div>
    </div>`;
}

export function buildBillPrintHtml({
  documentTitle, title,
  topStripTitle = "TAX INVOICE",
  company,
  partyLabel = "Bill To",
  partyRows, billRows,
  metaRows,
  columns, rows, totals,
  subTotalRow,
  footerNote,
  itemColumnWidth = "30%",
  minItemRows = 18,
  netAmount,
}) {
  const pr = partyRows || (metaRows ? metaRows.slice(0, Math.ceil(metaRows.length / 2)) : []);
  const br = billRows  || (metaRows ? metaRows.slice(Math.ceil(metaRows.length / 2)) : []);
  const rawNet = netAmount != null ? Number(netAmount) :
    Number(((totals || []).slice(-1)[0]?.value ?? "0").replace(/[^\d.]/g, ""));
  const words = amountToWords(isNaN(rawNet) ? 0 : rawNet);

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${e(documentTitle || title)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { min-height: 100%; }
    body { font-family: Arial, sans-serif; font-size: 9px; color: #000;
           background: #777; margin: 0; padding: 18px; }

    .inv { width: 210mm; height: 297mm; margin: 0 auto; border: 1.2px solid #000;
           background:#fff; display:flex; flex-direction:column; }

    /* Top strip */
    .ts { display:flex; justify-content:space-between; align-items:center;
          padding:2mm 3mm; border-bottom:1px solid #000; }
    .ts-title { font-size:11px; font-weight:800; letter-spacing:.4px; }
    .ts-copy  { font-size:8px; font-style:italic; }

    /* Company header */
    .co-hdr { display:grid; grid-template-columns:1fr auto; align-items:start;
              padding:4mm 5mm; border-bottom:1px solid #000; gap:5mm; }
    .co-brand { display:grid; grid-template-columns:auto 1fr; align-items:center; gap:4mm; }
    .co-logo { max-width:28mm; max-height:18mm; object-fit:contain; display:block; }
    .co-name { font-size:18px; font-weight:800; margin-bottom:2px; text-transform:uppercase; }
    .co-det  { font-size:8.5px; color:#222; line-height:1.35; }
    .doc-lbl { font-size:10px; font-weight:700; text-transform:uppercase;
               letter-spacing:.5px; text-align:right; white-space:nowrap; }

    /* Party / Bill info */
    .info-row { display:grid; grid-template-columns:1.18fr .82fr; border-bottom:1px solid #000; }
    .ic       { padding:3mm 4mm; min-height:28mm; }
    .ic + .ic { border-left:1px solid #000; }
    .ic-hd    { font-size:8px; font-weight:700; text-transform:uppercase;
                padding-bottom:2px; margin-bottom:2px; border-bottom:1px dashed #bbb; }
    table.it  { width:100%; border-collapse:collapse; }
    table.it td { padding:1px 2px; font-size:8.5px; border:none; vertical-align:top; line-height:1.25; }
    .il { color:#555; width:74px; white-space:nowrap; }
    .iv { font-weight:600; }
    .muted { color:#777; font-weight:400; }

    /* Items table */
    .iw { border-bottom:1px solid #000; flex:1 1 auto; min-height:0; }
    table.it2 { width:100%; border-collapse:collapse; }
    table.it2 thead th { border:1px solid #000; padding:3px 4px; font-size:8px;
                         font-weight:700; background:#ececec; text-align:left; }
    table.it2 tbody td { border-left:1px solid #000; border-right:1px solid #000;
                         border-bottom:0; padding:3px 4px; font-size:8.5px; vertical-align:top; }
    table.it2 tbody tr:first-child td { border-top:1px solid #000; }
    table.it2 tbody tr.fill-row td { height:8.5mm; padding:0 4px; }
    table.it2 tfoot td { border:1px solid #000; padding:3px 4px; font-size:8.5px;
                         font-weight:700; background:#ececec; }
    .r  { text-align:right; }
    .c  { text-align:center; }
    .item-col      { width:${e(itemColumnWidth)}; }
    .item-name-cell{ word-break:break-word; }
    .sub { display:block; font-size:7px; color:#444; margin-top:1px; line-height:1.3; }

    /* Bottom */
    .btm { display:grid; grid-template-columns:1fr 58mm; border-bottom:1px solid #000; flex:0 0 auto; }
    .wc  { padding:3mm 4mm; border-right:1px solid #000; }
    .wt  { font-size:8px; font-weight:700; text-transform:uppercase; margin-bottom:2px; }
    .wv  { font-size:8.5px; font-style:italic; }
    .terms-panel { margin-top:3mm; padding-top:2mm; border-top:1px dashed #bbb; }
    .terms-list { display:grid; gap:1px; font-size:8px; line-height:1.35; color:#222; }
    .tc  { min-width:0; }
    table.tot { width:100%; border-collapse:collapse; }
    table.tot td { padding:2px 4mm; font-size:8.5px; border-bottom:1px solid #d8d8d8; }
    .tl { color:#444; }
    .tv { text-align:right; font-weight:600; white-space:nowrap; min-width:22mm; }
    tr.tnet td { font-weight:800; font-size:10px; border-top:1.2px solid #000;
                 border-bottom:none; padding-top:3px; }

    .bank-sign { display:grid; grid-template-columns:1fr 1.35fr; min-height:29mm; flex:0 0 auto; }
    .bank-box { padding:3mm 4mm; border-right:1px solid #000; }
    .sign-box { display:grid; grid-template-columns:1fr 1fr; }
    .seal-box, .auth-box { display:flex; flex-direction:column; justify-content:flex-end;
                           min-height:29mm; padding:3mm 4mm; text-align:center; }
    .seal-box { border-right:1px solid #000; }
    .sign-company { font-size:8px; font-weight:700; text-align:left; margin-bottom:auto; }
    .sign-label { font-size:8px; font-weight:700; }
    .thank { border-top:1px solid #000; text-align:center; font-size:8px; font-weight:700; padding:1mm; }

    .pbtn { position:fixed; top:10px; right:10px; height:30px; padding:0 16px;
            border:1.5px solid #000; border-radius:4px; background:#fff;
            cursor:pointer; font-size:11px; font-weight:700; }

    @media print {
      @page { size:A4 portrait; margin:0; }
      html, body { width:210mm; min-height:297mm; background:#fff; }
      body  { padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      .no-print { display:none !important; }
      .inv { width:210mm; height:297mm; margin:0; border:1.2px solid #000; }
      tr { break-inside:avoid; }
    }
  </style>
</head>
<body>
  <button class="pbtn no-print" onclick="window.print()">&#128424; Print</button>

  <div class="inv">

    <div class="ts">
      <span></span>
      <span class="ts-title">${e(topStripTitle)}</span>
      <span class="ts-copy">Original / Duplicate</span>
    </div>

    <div class="co-hdr">
      <div>${renderCompanyBlock(company)}</div>
      <div class="doc-lbl">${e(title)}</div>
    </div>

    <div class="info-row">
      <div class="ic">
        <div class="ic-hd">${e(partyLabel)}</div>
        <table class="it"><tbody>${renderInfoRows(pr)}</tbody></table>
      </div>
      <div class="ic">
        <div class="ic-hd">Bill Details</div>
        <table class="it"><tbody>${renderInfoRows(br)}</tbody></table>
      </div>
    </div>

    <div class="iw">
      <table class="it2">
        <thead><tr>${renderHeaderCells(columns)}</tr></thead>
        <tbody>${renderBodyRows(rows, minItemRows, (columns || []).length)}</tbody>
        ${renderSubTotal(subTotalRow)}
      </table>
    </div>

    <div class="btm">
      <div class="wc">
        <div class="wt">Amount in Words</div>
        <div class="wv">${e(words)}</div>
        ${renderTerms(footerNote)}
      </div>
      <div class="tc">
        <table class="tot"><tbody>${renderTotals(totals)}</tbody></table>
      </div>
    </div>

    <div class="bank-sign">
      <div class="bank-box">
        <div class="wt">Bank Details</div>
        <table class="it"><tbody>${renderBankRows(company)}</tbody></table>
      </div>
      <div class="sign-box">
        <div class="seal-box">
          <div class="sign-label">Consignee Seal</div>
        </div>
        <div class="auth-box">
          <div class="sign-company">For ${e(company?.DisplayName || company?.CompanyName || "")}</div>
          <div class="sign-label">Authorised Signatory</div>
        </div>
      </div>
    </div>
    <div class="thank">THANK YOU</div>

  </div>
</body>
</html>`;
}

export function printBillHtml(html, onError) {
  try {
    const existing = document.getElementById("bill-print-preview-modal");
    if (existing) existing.remove();

    const modal = document.createElement("div");
    modal.id = "bill-print-preview-modal";
    modal.innerHTML = `
      <div class="bill-preview-backdrop" data-bill-preview-close="true"></div>
      <div class="bill-preview-dialog" role="dialog" aria-modal="true" aria-label="Print preview">
        <div class="bill-preview-toolbar">
          <strong>Print Preview</strong>
          <div class="bill-preview-actions">
            <button type="button" class="bill-preview-btn bill-preview-print">Print</button>
            <button type="button" class="bill-preview-btn bill-preview-close">Close</button>
          </div>
        </div>
        <iframe class="bill-preview-frame" title="Bill print preview"></iframe>
      </div>
    `;

    const style = document.createElement("style");
    style.textContent = `
      #bill-print-preview-modal,
      #bill-print-preview-modal * { box-sizing: border-box; }
      #bill-print-preview-modal { position: fixed; inset: 0; z-index: 100000; font-family: Arial, sans-serif; }
      #bill-print-preview-modal .bill-preview-backdrop { position: absolute; inset: 0; background: rgba(17, 24, 39, .72); }
      #bill-print-preview-modal .bill-preview-dialog {
        position: absolute; inset: 18px; display: flex; flex-direction: column;
        background: #f3f4f6; border-radius: 8px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.35);
      }
      #bill-print-preview-modal .bill-preview-toolbar {
        height: 52px; flex: 0 0 52px; display: flex; align-items: center; justify-content: space-between;
        padding: 0 16px; background: #fff; border-bottom: 1px solid #d1d5db; color: #111827; font-size: 15px;
      }
      #bill-print-preview-modal .bill-preview-actions { display: flex; gap: 8px; }
      #bill-print-preview-modal .bill-preview-btn {
        height: 34px; padding: 0 14px; border: 1px solid #cbd5e1; border-radius: 6px;
        background: #fff; color: #111827; font-size: 13px; font-weight: 700; cursor: pointer;
      }
      #bill-print-preview-modal .bill-preview-print { background: #111827; border-color: #111827; color: #fff; }
      #bill-print-preview-modal .bill-preview-frame { flex: 1; width: 100%; border: 0; background: #777; }
      @media (max-width: 768px) {
        #bill-print-preview-modal .bill-preview-dialog { inset: 8px; }
        #bill-print-preview-modal .bill-preview-toolbar { height: auto; min-height: 52px; gap: 10px; flex-wrap: wrap; padding: 10px 12px; }
      }
    `;
    modal.appendChild(style);
    document.body.appendChild(modal);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const iframe = modal.querySelector(".bill-preview-frame");
    const printButton = modal.querySelector(".bill-preview-print");
    const closeButton = modal.querySelector(".bill-preview-close");
    const previewHtml = html.replace("</style>", ".pbtn{display:none!important;}</style>");

    const close = () => {
      document.removeEventListener("keydown", onKeydown);
      document.body.style.overflow = previousOverflow;
      modal.remove();
    };
    const onKeydown = (event) => {
      if (event.key === "Escape") close();
    };

    iframe.srcdoc = previewHtml;
    printButton.addEventListener("click", () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error(err);
        if (onError) onError("Failed to open the print dialog.");
      }
    });
    closeButton.addEventListener("click", close);
    modal.addEventListener("click", (event) => {
      if (event.target?.dataset?.billPreviewClose) close();
    });
    document.addEventListener("keydown", onKeydown);
  } catch (err) {
    console.error(err);
    if (onError) onError("Failed to open the print preview modal.");
  }
}
